import { Fragment, useMemo, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { brandAssets } from '@eis/theme';
import {
  groupNavBySection,
  isFlagEnabled,
  useBranding,
  type NavEntry,
  type NavSection,
} from '@eis/core';
import { FeatherIcon, usePermissions } from '@eis/patterns';
import { activeModuleRegistry } from '../app/modules';
import { resolveIcon, type NavIconComponent } from './icons';
import { useChromeVariant } from './useChromeVariant';
import { useReturnToSuite } from './useSuite';

/** The legacy's own Home glyph; Berry keeps its folder (icon-map.json). */
const HOME_ICON = 'feather:home';
/** Back to the suite: the first thing in the drawer of a window opened from the legacy admin. */
const BACK_GLYPH = 'arrow-left';

/**
 * The left nav is *generated* from module manifests and filtered by the same permission
 * declarations that guard the routes (Addendum A.2, enforcement layer 1).
 *
 * This is the structural fix for the legacy duplication between `main-routes.ts` and
 * `menu-items.tsx`: there is no second list to drift out of sync, so a user can never be shown a
 * menu entry that leads to a 403.
 *
 * The `berry` variant wears the EIS Design System 2025 (T-31, prompt 24 Phase 24.2) and changes how
 * it LOOKS, not what it contains: a navy rail with the EMIL logo tile, the manifest sections as
 * expandable groups whose entries sit indented on the sub-level navy, the active entry in
 * `surfaces.navActive` with a 5 px dot instead of a pill, and a collapsed icon rail. Same entries,
 * same targets, same filtering. Back to the Insurance Suite is in this chrome too (Phase 24.5):
 * the Suite opens the console on berry as well.
 *
 * The `suite` chrome variant (SDD-012, U-2/U-4) changes the look once more and the contents not
 * at all: the legacy's navy drawer at 264 px, its Feather glyphs, an active item marked by the
 * tenant accent and a 3 px bar at the left edge instead of a pill, and the suite's brand block.
 * Everything variant-specific reads `theme.eis` and the palette's `surfaces.nav*` tokens; the
 * entries, their targets and their filtering are the same code in both.
 */
export function AppNav({ collapsed }: { readonly collapsed: boolean }): ReactNode {
  const { t } = useTranslation(['shell', 'insurance']);
  const permissions = usePermissions();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const chrome = useChromeVariant();
  const suite = chrome.variant === 'suite';
  const returnToSuite = useReturnToSuite();
  const branding = useBranding();
  // The tenant logo is the legacy's served file (U-7'); if it does not load, the bundled EMIL logo.
  const [logoFailed, setLogoFailed] = useState(false);
  // Berry's expandable groups, by section title. Every group starts open — the design draws them
  // open, and an entry hidden behind a closed group on the start page is one nobody finds.
  const [closedGroups, setClosedGroups] = useState<ReadonlySet<string>>(() => new Set());
  const toggleGroup = (title: string): void => {
    setClosedGroups((current) => {
      const next = new Set(current);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  const sections = useMemo(
    () =>
      groupNavBySection(
        activeModuleRegistry().visibleNav(permissions, (flag) => isFlagEnabled(flag, true)),
      ),
    [permissions],
  );

  const width = collapsed ? chrome.navWidthCollapsed : chrome.navWidth;

  /**
   * One item, in both widths.
   *
   * Collapsed, the label leaves the DOM but not the accessibility tree: the button keeps it as its
   * accessible name and a tooltip surfaces it on hover and on keyboard focus. That is what lets
   * the BDD steps — and a screen-reader user — find "Gruppen-Policen" in either state.
   */
  const isSelected = (to: string): boolean =>
    to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);

  /**
   * Berry's active entry: the label in `surfaces.navActive`, semi-bold, and a 5 px dot at the end
   * of the row — no fill. On a sub-item the row keeps the sub-level navy under it.
   */
  const berrySelected = {
    bgcolor: 'transparent',
    color: 'surfaces.navActive',
    '& .MuiListItemIcon-root': { color: 'surfaces.navActive' },
    '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
    '&::after': collapsed
      ? undefined
      : {
          content: '""',
          width: 5,
          height: 5,
          ml: 1,
          flexShrink: 0,
          borderRadius: '50%',
          bgcolor: 'surfaces.navActive',
        },
  } as const;

  const renderItem = (
    key: string,
    label: string,
    to: string,
    Icon: NavIconComponent,
    nested = false,
  ): ReactNode => {
    const selected = isSelected(to);
    return (
      <ListItem
        key={key}
        disablePadding
        sx={{ px: suite ? 0 : collapsed ? 1 : 0, bgcolor: nested ? 'surfaces.navSub' : undefined }}
      >
        <Tooltip title={collapsed ? label : ''} placement="right">
          <ListItemButton
            component={Link}
            to={to}
            selected={selected}
            aria-label={label}
            // The page's own entry, announced as such (and what E2E reads in berry, which has
            // no breadcrumb trail to say where you are).
            aria-current={selected ? 'page' : undefined}
            sx={{
              minHeight: 40,
              justifyContent: collapsed ? 'center' : 'flex-start',
              px: collapsed ? 1 : suite ? 2.25 : 2,
              pl: nested ? 6.25 : undefined,
              borderRadius: suite || collapsed ? undefined : 0,
              ...(!suite && { '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' } }),
              color: 'surfaces.navText',
              '& .MuiListItemIcon-root': { color: 'surfaces.navText' },
              /**
               * Berry marks the active entry in the nav-active colour with a dot (above) — a
               * declared token pair asserted in the contrast test, never a translucent overlay
               * whose composite depends on the surface below.
               *
               * The suite marks it the legacy way: no fill, the label and glyph in the tenant
               * accent, and a 3 px bar at the drawer's left edge (`nav.activeIndicator`).
               */
              '&.Mui-selected': suite
                ? {
                    bgcolor: 'transparent',
                    color: 'surfaces.navActive',
                    '& .MuiListItemIcon-root': { color: 'surfaces.navActive' },
                    '&:hover': { bgcolor: 'transparent' },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 4,
                      bottom: 4,
                      width: 3,
                      bgcolor: 'surfaces.navActive',
                    },
                  }
                : berrySelected,
            }}
          >
            {!nested && (
              <ListItemIcon sx={{ minWidth: collapsed ? 0 : 34, justifyContent: 'center' }}>
                <Icon fontSize="small" />
              </ListItemIcon>
            )}
            {!collapsed && (
              <ListItemText
                primary={label}
                slotProps={{
                  primary: {
                    variant: 'body2',
                    // The legacy weighs every item the same; berry emphasises the active one.
                    sx: { fontWeight: suite ? 400 : selected ? 600 : 400, fontSize: '0.875rem' },
                  },
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  };

  const renderEntry = (entry: NavEntry, nested = false): ReactNode =>
    // Manifest labels are namespaced keys contributed by modules the shell cannot know
    // statically, so this is the one place typed-key checking is relaxed. NavEntry.label
    // being `${string}:${string}` keeps the namespace convention enforced.
    renderItem(
      entry.to,
      t(entry.label as never),
      entry.to,
      resolveIcon(entry.icon, chrome.variant),
      nested,
    );

  /**
   * Berry, expanded: a section is a group — a button with the section's name, the first entry's
   * icon and a chevron, over its entries indented on the sub-level navy. The button states the
   * group's state with `aria-expanded`; the entries stay links in their own `<ul>`.
   */
  const renderGroup = (section: NavSection & { readonly title: string }): ReactNode => {
    const title = t(section.title as never);
    const open = !closedGroups.has(section.title);
    const GroupIcon = resolveIcon(section.entries[0]?.icon ?? '', chrome.variant);
    const listId = `nav-group-${section.title.replace(/[^a-z0-9]+/gi, '-')}`;
    return (
      <Fragment key={section.title}>
        <List disablePadding>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                toggleGroup(section.title);
              }}
              aria-expanded={open}
              aria-controls={listId}
              sx={{
                minHeight: 40,
                px: 2,
                borderRadius: 0,
                color: 'surfaces.navSection',
                '& .MuiListItemIcon-root': { color: 'surfaces.navSection' },
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 34, justifyContent: 'center' }}>
                <GroupIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={title}
                slotProps={{ primary: { variant: 'body2', sx: { fontSize: '0.875rem' } } }}
              />
              <ExpandMoreIcon
                fontSize="small"
                aria-hidden
                sx={{
                  transition: (theme) => theme.transitions.create('transform', { duration: 150 }),
                  transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
        <Collapse in={open} unmountOnExit>
          <List disablePadding id={listId}>
            {section.entries.map((entry) => renderEntry(entry, true))}
          </List>
        </Collapse>
      </Fragment>
    );
  };

  /**
   * A section is a heading plus its own `<ul>`. Several lists inside one `<nav>` is the shape a
   * screen reader wants — one list per group — and it avoids nesting a heading inside an `<li>`.
   */
  const renderSection = (section: NavSection, index: number): ReactNode =>
    !suite && !collapsed && section.title !== undefined ? (
      renderGroup({ ...section, title: section.title })
    ) : (
      <Fragment key={section.title ?? `__unsectioned-${String(index)}`}>
        {section.title !== undefined &&
          (collapsed ? (
            // A heading with no room for its text would be an unlabelled rule; say nothing instead.
            <Divider sx={{ mx: 2, my: 1 }} />
          ) : (
            <Typography
              variant="overline"
              component="span"
              sx={{
                display: 'block',
                px: 3,
                pt: 1.5,
                pb: 0.5,
                color: 'surfaces.navSection',
                // The tokens file's section label: 10.5 px uppercase, letter-spaced (`nav.sectionLabel`).
                ...(suite && { fontSize: '0.65625rem', letterSpacing: '0.12em', fontWeight: 700 }),
              }}
            >
              {t(section.title as never)}
            </Typography>
          ))}
        <List disablePadding>{section.entries.map((entry) => renderEntry(entry))}</List>
      </Fragment>
    );

  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        flexShrink: 0,
        // The rail/menu swap is the only animation in the shell; anything longer feels sluggish
        // on a toggle a user hits several times a day.
        transition: (theme) => theme.transitions.create('width', { duration: 200 }),
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          bgcolor: 'surfaces.nav',
          overflowX: 'hidden',
          transition: (theme) => theme.transitions.create('width', { duration: 200 }),
        },
      }}
    >
      <Box
        sx={{
          height: chrome.headerHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          px: collapsed ? 0 : suite ? 2.25 : 2.5,
          flexShrink: 0,
        }}
      >
        {suite ? (
          /**
           * The legacy brand block: the tenant logo in a 34 px box and the suite's name in white.
           * The logo is the legacy's own served file (runtime config, U-7'), so both windows show
           * the same image; the frozen EMIL asset is the fallback when it cannot be loaded.
           */
          <Box
            component="img"
            src={logoFailed ? brandAssets.emilLogo : branding.logo}
            onError={() => {
              setLogoFailed(true);
            }}
            alt=""
            aria-hidden
            data-testid="brand-logo"
            sx={{ width: 34, height: 34, flexShrink: 0, objectFit: 'contain' }}
          />
        ) : (
          /**
           * Berry's brand block (EIS Design System): the EMIL logo tile, 36 px at the badge radius,
           * and the suite's name on two lines. The console no longer names itself "EIS Console" in
           * the rail — the product is the Insurance Suite; the window title still says where you are.
           */
          <Box
            component="img"
            src={brandAssets.emilLogo}
            alt=""
            aria-hidden
            data-testid="brand-logo"
            sx={{
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: (theme) => `${String(theme.eis.surface.badgeRadius)}px`,
            }}
          />
        )}
        {!collapsed && (
          <Typography
            variant="subtitle1"
            component="span"
            sx={{
              ml: 1.5,
              ...(suite
                ? {
                    whiteSpace: 'nowrap',
                    color: 'common.white',
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                  }
                : {
                    maxWidth: 120,
                    color: 'surfaces.navSection',
                    fontSize: '0.8125rem',
                    lineHeight: 1.3,
                    fontWeight: 600,
                  }),
            }}
          >
            {t('shell:app.suiteName')}
          </Typography>
        )}
      </Box>
      {!suite && <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />}
      <Box component="nav" aria-label={t('shell:nav.label')} sx={{ py: 1, overflowY: 'auto' }}>
        {suite && (
          /**
           * Back to the Insurance Suite (U-5'): the first element of the sidebar, Board 2 and 4 of
           * the hand-off sketches. A button, not a link — where it goes depends on whether this
           * window has an opener, and a link whose href lied about that would be worse than none.
           */
          <List disablePadding sx={{ mb: 1 }}>
            <ListItem disablePadding sx={{ px: collapsed ? 1 : 1.75 }}>
              <Tooltip title={collapsed ? t('shell:back.toSuite') : ''} placement="right">
                <ListItemButton
                  component="button"
                  type="button"
                  onClick={() => {
                    returnToSuite();
                  }}
                  aria-label={t('shell:back.toSuite')}
                  data-testid="back-to-suite"
                  sx={{
                    minHeight: 36,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    px: collapsed ? 1 : 1.5,
                    border: 1,
                    borderColor: 'rgba(255,255,255,0.28)',
                    borderRadius: 1.5,
                    color: 'common.white',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 0 : 30,
                      justifyContent: 'center',
                      color: 'inherit',
                    }}
                  >
                    <FeatherIcon glyph={BACK_GLYPH} fontSize="small" />
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={t('shell:back.toSuite')}
                      slotProps={{ primary: { variant: 'body2', sx: { fontWeight: 600 } } }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          </List>
        )}
        {!suite && (
          /**
           * Back to the Insurance Suite on berry (prompt 24 Phase 24.5): the Suite opens the
           * console in this chrome too, so the way back cannot live in suite's drawer only. A
           * quiet first row rather than suite's outlined button — the rail's own row style.
           */
          <List disablePadding>
            <ListItem disablePadding sx={{ px: collapsed ? 1 : 0 }}>
              <Tooltip title={collapsed ? t('shell:back.toSuite') : ''} placement="right">
                <ListItemButton
                  component="button"
                  type="button"
                  onClick={() => {
                    returnToSuite();
                  }}
                  aria-label={t('shell:back.toSuite')}
                  data-testid="back-to-suite"
                  sx={{
                    minHeight: 40,
                    width: '100%',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    px: collapsed ? 1 : 2,
                    borderRadius: collapsed ? undefined : 0,
                    color: 'surfaces.navText',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 0 : 34,
                      justifyContent: 'center',
                      color: 'inherit',
                    }}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={t('shell:back.toSuite')}
                      slotProps={{ primary: { variant: 'body2', sx: { fontSize: '0.875rem' } } }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          </List>
        )}
        <List disablePadding>
          {renderItem('__home', t('shell:nav.home'), '/', resolveIcon(HOME_ICON, chrome.variant))}
        </List>
        {sections.map(renderSection)}
        {sections.length === 0 && !collapsed && (
          <Typography
            variant="caption"
            sx={{ display: 'block', px: 2.5, color: suite ? 'text.secondary' : 'surfaces.navText' }}
          >
            {t('shell:nav.noProducts')}
          </Typography>
        )}
      </Box>
    </Drawer>
  );
}
